/**
 * 端末に残す設定(難易度トグル・音量・ことばの設定)。
 *
 * 難易度はプレイごとではなく**全体設定として保存**する(docs/02_game_design.md §1)。
 * ことばの設定は海外展開の土台(docs/10_paltown_platform.md §1)。
 */

import type { Difficulty } from '@paltyping/engine';
import type { KeyValueStorage } from './progress';
import { appStorage } from './userState';
import { isValidDisplayName, normalizeDisplayName } from './profileName';
import {
  DEFAULT_FIRST_LANGUAGE,
  defaultLearningLanguages,
  isLanguageCode,
  normalizeLearningLanguages,
  type LanguageCode,
} from './languages';

export interface AppSettings {
  readonly difficulty: Difficulty;
  /** 0〜1。 */
  readonly volume: number;
  readonly muted: boolean;
  /** 第一言語(母語)。UI・訳の表示に使う想定。 */
  readonly firstLanguage: LanguageCode;
  /** 学びたい言語(複数可)。第一言語は含まない。 */
  readonly learningLanguages: readonly LanguageCode[];
  /**
   * うまれた年と月(`YYYY-MM`)。**任意**。未設定は `null`。
   *
   * チャレンジのランキングを年齢別(1歳ごと)に分けるためだけに使う。
   * **日は持たない**(docs/12_challenge_ranking.md §1「PII最小化」)。
   * 権威はサーバー(`users/{uid}/profile`)で、ここはその控え。
   */
  readonly birthYearMonth: string | null;
  /**
   * ランキング・リーグに出す**なまえ**(docs/12_challenge_ranking.md §1)。
   *
   * 権威はサーバー(`users/{uid}/profile/main.displayName`)で、ここはその控え
   * (次に開いたとき、通信を待たずに いまの なまえを出せるようにするため)。
   * 未設定(`null`)なら順位表には既定の「ぱるっこ」で出る。
   */
  readonly displayName: string | null;
  /**
   * 「がんばりを いろに はんえいする」(docs/14_characters.md §2)。
   *
   * **既定はオフ = 白いパル**。オンにすると直近の取り組み量の比率で体色が混ざる。
   * リーグなど他人から見える場面にも同じ設定が効く(いまはローカル表示だけ)。
   */
  readonly reflectEffortColor: boolean;
  /**
   * まなびの**カリキュラム**(docs/13_learn_module.md §9 のカリキュラムパック)。
   *
   * `content/v1/learn/{ここの値}/pack.json` が「どの学年で何を習うか」の宣言。
   * 既定は日本(`jp-mext`)。**選択は無料・無制限**で価格には一切影響しない
   * (docs/10 §4。価格は支払い手段の国で決まる)。
   * パックが1つしか無いあいだは設定画面に選択UIを出さない。
   */
  readonly curriculum: string;
  /**
   * まなびの**もじの ひょうじ**(docs/13。ユーザー指示 2026-08-22)。
   *
   * `kanji`(既定)= 漢字版があれば ふりがな付きの漢字で出す / `hiragana` = かな中心。
   * 学習言語に日本語が入っている子にだけ設定画面に出す(`usesJapanese`)。
   * 効くのは問題文・選択肢・解説で、単元名やUI文言は かなのまま。
   */
  readonly japaneseScript: JapaneseScript;
  /**
   * 「パルの おてつだい」(docs/07_learning_support.md §7.4)。**既定はオン**。
   *
   * オフにすると詰まり検知・ヒント・応援の吹き出しを**全部止める**
   * (docs/07 §3.4「介入頻度に上限、設定でOFF/控えめ可」)。
   * ひとりで じっくり 考えたい子・見られたくない子のための逃げ道なので、
   * 「こたえを みる」ボタンごと出さない。
   */
  readonly palHelp: boolean;
}

/**
 * もじの ひょうじ。
 *
 * `@paltyping/quiz` の `JapaneseScript` と同じ値をここに写している。理由は
 * `CURRICULUM_ID` と同じで、**タイピングの初回バンドルに `@paltyping/quiz` を
 * 載せないため**(この設定はタイトル画面から読まれる。CLAUDE.md 鉄則6)。
 * ずれは `settings.test.ts` が本体と突き合わせて落とす。
 */
export type JapaneseScript = 'hiragana' | 'kanji';

/** すべてのもじの ひょうじ。 */
export const JAPANESE_SCRIPTS: readonly JapaneseScript[] = ['hiragana', 'kanji'];

/**
 * 既定のもじの ひょうじ(ユーザー指示 2026-08-25 で **漢字** へ)。
 *
 * 漢字にはかならず ふりがな が付く(`quiz-ruby.ts` の区切り)ので、
 * 読めない漢字で詰まることは無い。かなで読みたい子は設定で戻せる。
 * **保存済みの選択はそのまま尊重**する(この既定が効くのは
 * `japaneseScript` を1度も保存していない子だけ)。
 */
export const DEFAULT_JAPANESE_SCRIPT: JapaneseScript = 'kanji';

/** 保存値がもじの ひょうじか(知らない値は既定へ丸める)。 */
export function isJapaneseScript(value: unknown): value is JapaneseScript {
  return typeof value === 'string' && (JAPANESE_SCRIPTS as readonly string[]).includes(value);
}

/**
 * カリキュラムID(`@paltyping/quiz` の `CURRICULUM_ID_PATTERN` と同じ形)。
 *
 * ここに写しているのは、**タイピングの初回バンドルに `@paltyping/quiz` を
 * 載せないため**(この設定はタイトル画面から読まれる。CLAUDE.md 鉄則6)。
 * ずれは `settings.test.ts` が本体と突き合わせて落とす。
 */
const CURRICULUM_ID = /^[a-z]{2}-[a-z0-9]+$/;

/** 既定のカリキュラム(日本の学習指導要領)。 */
export const DEFAULT_CURRICULUM = 'jp-mext';

/** カリキュラムIDの形か(保存値からの復元で使う)。 */
export function isCurriculum(value: unknown): value is string {
  return typeof value === 'string' && CURRICULUM_ID.test(value);
}

/** `YYYY-MM` の形か(1〜12月のみ)。 */
export function isBirthYearMonth(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = /^\d{4}-(\d{2})$/.exec(value);
  if (!match) return false;
  const month = Number(match[1]);
  return month >= 1 && month <= 12;
}

export const DEFAULT_SETTINGS: AppSettings = {
  difficulty: 'easy',
  volume: 0.6,
  muted: false,
  firstLanguage: DEFAULT_FIRST_LANGUAGE,
  learningLanguages: defaultLearningLanguages(DEFAULT_FIRST_LANGUAGE),
  birthYearMonth: null,
  displayName: null,
  reflectEffortColor: false,
  curriculum: DEFAULT_CURRICULUM,
  japaneseScript: DEFAULT_JAPANESE_SCRIPT,
  palHelp: true,
};

const STORAGE_KEY = 'paltyping.settings.v1';
const DIFFICULTIES: readonly Difficulty[] = ['easy', 'normal', 'hard'];

/** 壊れた値を既定に丸めて読む。 */
export function parseSettings(raw: string | null): AppSettings {
  if (!raw) return DEFAULT_SETTINGS;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_SETTINGS;
    const record = parsed as Partial<AppSettings>;
    const difficulty =
      typeof record.difficulty === 'string' &&
      (DIFFICULTIES as readonly string[]).includes(record.difficulty)
        ? (record.difficulty as Difficulty)
        : DEFAULT_SETTINGS.difficulty;
    const volume =
      typeof record.volume === 'number' && Number.isFinite(record.volume)
        ? Math.min(1, Math.max(0, record.volume))
        : DEFAULT_SETTINGS.volume;
    const firstLanguage = isLanguageCode(record.firstLanguage)
      ? record.firstLanguage
      : DEFAULT_SETTINGS.firstLanguage;
    // 配列そのものが無い(= 旧バージョンの保存データ)ときだけ既定に戻す。
    // ユーザーが自分で全部はずした空配列は、そのまま尊重する。
    const learningLanguages = Array.isArray(record.learningLanguages)
      ? normalizeLearningLanguages(firstLanguage, record.learningLanguages)
      : defaultLearningLanguages(firstLanguage);
    return {
      difficulty,
      volume,
      muted: record.muted === true,
      firstLanguage,
      learningLanguages,
      birthYearMonth: isBirthYearMonth(record.birthYearMonth) ? record.birthYearMonth : null,
      // 決まりに合わない控え(手で書き換えた・昔の上限で保存した)は捨てる。
      // サーバーが権威なので、消えても次の変更でまた入る。
      displayName:
        typeof record.displayName === 'string' && isValidDisplayName(record.displayName)
          ? normalizeDisplayName(record.displayName)
          : null,
      // 無い(= この設定より前の保存データ)ときは既定のオフ = 白いパル。
      reflectEffortColor: record.reflectEffortColor === true,
      // 知らないカリキュラムが入っていたら既定に戻す(配信が消えたパックを掴んだまま
      // 「じゅんびちゅう」だけの画面になるのを防ぐ)。
      curriculum: isCurriculum(record.curriculum) ? record.curriculum : DEFAULT_CURRICULUM,
      // 無い(= この設定より前の保存データ・まだ選んでいない)ときだけ既定の漢字。
      // 自分で かな を選んだ子の保存値は そのまま残る。
      japaneseScript: isJapaneseScript(record.japaneseScript)
        ? record.japaneseScript
        : DEFAULT_JAPANESE_SCRIPT,
      // 無い(= この設定より前の保存データ)ときは既定のオン
      // (おてつだいは既定で出る。切りたい子だけが切る)。
      palHelp: record.palHelp !== false,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

/**
 * 第一言語を変える(整合ガード付き)。
 * 新しい第一言語は学びたい言語から自動で外し、空になったら既定に戻す
 * (例: にほんご→English に変えると、学びたい言語は にほんご になる)。
 */
export function withFirstLanguage(settings: AppSettings, firstLanguage: LanguageCode): AppSettings {
  return {
    ...settings,
    firstLanguage,
    learningLanguages: normalizeLearningLanguages(firstLanguage, settings.learningLanguages, true),
  };
}

/** 学びたい言語のチェックを1つ入れ替える(第一言語は選べない)。 */
export function withLearningLanguageToggled(
  settings: AppSettings,
  code: LanguageCode,
): AppSettings {
  if (code === settings.firstLanguage) return settings;
  const has = settings.learningLanguages.includes(code);
  const next = has
    ? settings.learningLanguages.filter((item) => item !== code)
    : [...settings.learningLanguages, code];
  return {
    ...settings,
    learningLanguages: normalizeLearningLanguages(settings.firstLanguage, next),
  };
}

export function loadSettings(storage: KeyValueStorage | null = appStorage()): AppSettings {
  return parseSettings(storage?.getItem(STORAGE_KEY) ?? null);
}

export function saveSettings(
  settings: AppSettings,
  storage: KeyValueStorage | null = appStorage(),
): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // 保存できなくてもプレイは続行できる。
  }
}
